import { ReactNode } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import styled, { css } from 'styled-components';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  InfoList, InfoListItem, Tooltip, TooltipInfoIcon, WarningInfo,
} from '../../../..';
import { Flex } from '../../Flex';

export const InputFocus = css` ${({
  theme,
}) => `
  &:focus {  
    box-shadow: 0px 0px 0px 5px ${theme.colours.info[1]};
    outline: 2px solid ${theme.colours.neutral[10]};
    border: solid 1px ${theme.colours.primary[2]};
  }
`}`;

export const BorderlessInputFocus = css`
  ${InputFocus} 
  &:focus {
    border: none;
  }
`;

export const InputContainer = styled.div<{ styles?: string, inline?: boolean }>` ${({ theme, styles, inline }) => `
  color:${theme.textPrimary};
  display:flex;
  flex-direction: ${inline ? 'row' : 'column'};
  justify-content: ${inline ? 'space-between' : 'center'};
  width:100%;
  box-sizing: border-box;
  height: 100%;
  ${inline ? `
    white-space: nowrap;
    align-items: center;
  ` : ''};

  ${styles || ''}
`}`;

export const InputLabel = styled.label<{ hasError?: boolean, styles?: string, required?: boolean, disabled?: boolean, readOnly?: boolean }>` ${({
  theme, hasError, styles, required, disabled, readOnly,
}) => `
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.padding[1]};
  color:${hasError ? theme.textDanger : theme.colours.neutral[2]};
  color:${(() => {
    if (readOnly) return theme.colours.neutral[2];
    if (disabled) return theme.colours.neutral[2];
    if (hasError) return theme.textDanger;
    return theme.colours.neutral[2];
  })()};
  font-size:${theme.fontSize[2]};
  font-weight:${theme.fontWeight.bold};
  margin-bottom:${theme.padding[1]};
  line-height: ${theme.fontSize[6]};
  ${!required ? `
    &:after {
      content: " (Optional)";
      color: ${theme.colours.neutral[5]};
    }
  ` : ''}
  ${styles || ''}
`}`;

const ChildrenInputContainer = styled.div<{ inline?: boolean }>`${({
  inline,
}) => `
  width: ${inline ? '50%' : '100%'};
`}`;

export const InputErrorText = styled.p<{ styles?: string, hasError?: boolean }>`${({ theme, styles, hasError }) => `
  color:${theme.textDanger};
  height: 0px;
  width: 0px;
  white-space: nowrap;
  font-size:${theme.fontSize[1]};
  ${hasError && `margin: ${theme.padding[1]} 0 -${theme.padding[1]} 0;`}
  ${styles || ''}
`}`;

export const InputCss = css<{ styles?: string, hasError?: boolean, small?: boolean, xs?: boolean, readOnly?: boolean }>` ${({
  theme, styles = '', hasError = false, small = false, xs = false,
}) => `
  font-family: 'Open Sans', sans-serif;
  border: 1px solid ${hasError ? theme.colours.danger[1] : theme.colours.neutral[6]};
  border-radius: ${theme.borderRadius[1]};
  background:${theme.body};
  color:${theme.colours.neutral[2]};
  padding:${theme.padding[2]} ${theme.fontSize[2]};
  font-size:${xs ? theme.fontSize[1] : theme.fontSize[2]};
  width: 100%;
  height: ${(() => {
    if (small) return theme.fontSize[8];
    if (xs) return theme.padding[7];
    return theme.fontSize[9];
  })()};
  box-sizing: border-box;
  font-weight: ${theme.fontWeight.normal};  
  &:focus {
    outline: none;
  }
  
  ${styles}
`}`;

export const LabelContainer = styled(Flex)`
  align-items:center;
`;

const InputWarningInfo = styled.div`${({ theme }) => `
    padding-top: ${theme.padding[2]};
`}`;

const ErrorListContainer = styled.ul<{ itemCount: number, isTooltipError: boolean }>`${({ theme, itemCount, isTooltipError }) => `
    padding: 0;
    margin: 0;
    padding-left:${itemCount > 1 ? theme.padding[3] : 0};
    list-style: ${itemCount > 1 ? 'default' : 'none'};
    color:${isTooltipError ? theme.textSecondary : theme.colours.danger[1]};
    font-size:${theme.fontSize[1]};
`}`;

function ErrorList({ items, isTooltipError = false }: { items: string[], isTooltipError?: boolean }) {
  return (
    <ErrorListContainer itemCount={items.length} isTooltipError={isTooltipError}>
      {items.map((item) => (
        <InfoListItem key={item}>{item}</InfoListItem>
      ))}
    </ErrorListContainer>
  );
}

const StyledErrorTooltip = styled(ReactTooltip)`${({ theme }) => `
  border-radius:  ${theme.borderRadius[2]};
  opacity: 1;
  background: ${theme.colours.neutral[2]};
  padding: ${theme.padding[2]} ${theme.padding[3]};
`}`;

type InputProps = {
  children: ReactNode,
  id: string,
  label?: any,
  hasError?: boolean,
  containerStyles?: string;
  labelStyles?: string;
  required?: boolean;
  inline?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  tooltip?: ReactNode;
  isTooltipError?: boolean;
  hideErrorMessage?: boolean;
  errors?: string[];
  warnings?: string[];
};

export function Input({
  children,
  id,
  label,
  hasError = false,
  errors = [],
  warnings = [],
  containerStyles = '',
  labelStyles = '',
  required = false,
  inline = false,
  disabled = false,
  readOnly = false,
  tooltip = '',
  isTooltipError = false,
  hideErrorMessage = false,
}: InputProps) {
  const tooltipId = `tooltip-${id}`;
  const errorsTooltipId = `error-tooltip-${id}`;
  return (
    <InputContainer styles={containerStyles} inline={inline}>
      {label && (
        <LabelContainer>
          <InputLabel htmlFor={id} styles={labelStyles} required={required} disabled={disabled} readOnly={readOnly}>
            <div>{label}</div>
            {tooltip && (
              <>
                <TooltipInfoIcon id={tooltipId} tabIndex={-1} />
                <Tooltip id={tooltipId} place="top">{tooltip}</Tooltip>
              </>
            )}
          </InputLabel>

        </LabelContainer>
      )}
      <ChildrenInputContainer inline={inline} id={errorsTooltipId} data-tooltip-id={errorsTooltipId}>
        <Flex>
          {children}
        </Flex>

        {!hideErrorMessage && hasError && !!errors?.length && (
          !isTooltipError
            ? <ErrorList items={errors} />
            : (
              <StyledErrorTooltip id={errorsTooltipId} place="bottom">
                <ErrorList items={errors} isTooltipError />
              </StyledErrorTooltip>
            )
        )}

        {!!warnings?.length && (
          <InputWarningInfo>
            <WarningInfo>
              <InfoList items={warnings} />
            </WarningInfo>
          </InputWarningInfo>
        )}
      </ChildrenInputContainer>
    </InputContainer>
  );
}
